// @flow

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import MultiOptionSelect from '../../../../../elements/MultiOptionSelect/MultiOptionSelect';

import {useAdditionalInformationContext} from '../../context/hooks';
import {Typography} from '@material-ui/core';

import type {CheckoutInput, Order} from '../../../../../entities';

type Props = {
  errors: {[key: string]: {type: string, message: string}} | {},
  classes: {[key: string]: string},
  item: CheckoutInput,
  order?: Order,
};

const useStyles = makeStyles(theme => ({
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
}));

export function ChecklistInput(props: Props) {
  const {classes: propClasses, item, order} = props;

  const classes = useStyles();

  const {schema} = item;

  const [touched, setTouched] = useState<boolean>(false);

  const {
    additionalInformation,
    setAdditionalInformation,
    additionalIformationValidate,
  } = useAdditionalInformationContext();

  const onSelectOptions = (opt: string[]) => {
    setAdditionalInformation(prev => {
      const prevInputInfo = prev[item.id] ?? {};
      return {
        ...prev,
        [item.id]: {
          ...prevInputInfo,
          value: opt,
        },
      };
    });
  };

  const selectedOptions = useMemo(
    () => additionalInformation[item.id]?.value ?? [],
    [additionalInformation, item.id]
  );

  const withErrors = useMemo(() => {
    if (!item.required) return;
    const selectedOptionsCount = selectedOptions.length;
    const minOptions = schema?.min_selected_options;
    const maxOptions = schema?.max_selected_options;
    if (minOptions && selectedOptionsCount < minOptions)
      return `Debes escoger mínimo ${minOptions} opciones`;
    if (maxOptions && selectedOptionsCount > maxOptions)
      return `Debes escoger máximo ${maxOptions} opciones`;
  }, [item.required, selectedOptions.length, schema]);

  const orderAdditionalInfo = useMemo(() => {
    if (order && order.additionalInformation) {
      return order.additionalInformation.find(
        el => el.storeOrderAdditionalInformation.id === item.id
      );
    }
  }, [item, order]);

  const handleTouched = useCallback(() => {
    setTouched(true);
  }, []);

  useEffect(() => {
    setAdditionalInformation(prev => {
      const prevInputInfo = prev[item.id] ?? {};
      return {
        ...prev,
        [item.id]: {
          ...prevInputInfo,
          error: withErrors,
        },
      };
    });
  }, [item.id, setAdditionalInformation, withErrors]);

  useEffect(() => {
    if (orderAdditionalInfo) {
      const {additionalInformation} = orderAdditionalInfo;
      if (additionalInformation) {
        setAdditionalInformation(prev => {
          return {
            ...prev,
            [item.id]: {
              value: additionalInformation.value ?? [],
            },
          };
        });
      }
    }
  }, [item.id, orderAdditionalInfo, setAdditionalInformation]);

  return (
    <div className={propClasses.checklistWrap}>
      <div className={classes.label}>
        <span>{item.name}</span>
        <MultiOptionSelect
          onFocus={handleTouched}
          availableOptions={schema?.value ?? []}
          setOptions={onSelectOptions}
          selectedOptions={selectedOptions}
          minSelected={schema?.min_selected_options}
          maxSelected={schema?.max_selected_options}
        />
      </div>
      {(touched || additionalIformationValidate) && withErrors && (
        <Typography className={propClasses.error}>{withErrors}</Typography>
      )}
    </div>
  );
}
